// ===========================================
// END OF DAY
// ===========================================
// The nightly ritual: today's numbers, count the drawer, record over/short,
// close the day. One calm page, no spreadsheet.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Moon, Banknote, CheckCircle2, AlertCircle } from 'lucide-react';
import { eodApi } from '../services/api';
import { toast } from '../stores/toastStore';
import clsx from 'clsx';

const money = (cents: number) => `$${((cents || 0) / 100).toFixed(2)}`;
const METHOD_LABEL: Record<string, string> = {
  card_present: 'Card',
  cash: 'Cash',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  gift_card: 'Gift Card',
};

export default function EndOfDayPage() {
  const queryClient = useQueryClient();
  const today = new Date().toLocaleDateString('sv-SE'); // YYYY-MM-DD, local
  const tzOffsetMinutes = new Date().getTimezoneOffset();
  const [date, setDate] = useState(today);
  const [cashCounted, setCashCounted] = useState('');
  const [notes, setNotes] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['eod-summary', date],
    queryFn: async () => {
      const response = await eodApi.summary(date, tzOffsetMinutes);
      return response.data?.data;
    },
  });

  const { data: closures } = useQuery({
    queryKey: ['eod-closures'],
    queryFn: async () => {
      const response = await eodApi.list();
      return response.data?.data as any[];
    },
  });

  const closeMutation = useMutation({
    mutationFn: async () => {
      const response = await eodApi.close({
        date,
        tzOffsetMinutes,
        cashCountedCents: Math.round(parseFloat(cashCounted || '0') * 100),
        notes: notes.trim() || undefined,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success(`${date} is closed — good night.`);
      setCashCounted('');
      setNotes('');
      queryClient.invalidateQueries({ queryKey: ['eod-summary'] });
      queryClient.invalidateQueries({ queryKey: ['eod-closures'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || "Couldn't close the day — please try again.");
    },
  });

  const alreadyClosed = !!data?.closure;
  const cashExpected = data?.cashExpectedCents ?? 0;
  const countedCents = Math.round(parseFloat(cashCounted || '0') * 100);
  const overShort = countedCents - cashExpected;

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-safe max-w-3xl">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Moon className="w-6 h-6 text-primary-600" />
            End of Day
          </h1>
          <p className="text-slate-500 text-sm mt-1">Count the drawer, check the numbers, close the day.</p>
        </div>
        <input
          type="date"
          value={date}
          max={today}
          onChange={(e) => setDate(e.target.value)}
          className="input w-auto"
        />
      </div>

      {/* Day summary */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">{date === today ? "Today's numbers" : `Numbers for ${date}`}</h2>
        {isLoading ? (
          <p className="text-sm text-slate-500">Adding it up…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Sales</p>
                <p className="text-xl font-bold text-slate-900">{money(data?.totals?.salesCents)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Tips</p>
                <p className="text-xl font-bold text-slate-900">{money(data?.totals?.tipsCents)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Checks</p>
                <p className="text-xl font-bold text-slate-900">{data?.totals?.checksClosed ?? 0}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Refunded</p>
                <p className="text-xl font-bold text-slate-900">{money(data?.totals?.refundedCents)}</p>
              </div>
            </div>

            <div className="divide-y divide-slate-100 border-t border-slate-100">
              {Object.entries(data?.byMethod || {}).map(([method, m]: [string, any]) => (
                <div key={method} className="py-2.5 flex items-center justify-between text-sm">
                  <span className="text-slate-600">{METHOD_LABEL[method] || method} · {m.count} payment{m.count === 1 ? '' : 's'}</span>
                  <span className="font-semibold text-slate-900">{money(m.totalCents)}</span>
                </div>
              ))}
              {Object.keys(data?.byMethod || {}).length === 0 && (
                <p className="py-3 text-sm text-slate-400">No payments recorded for this day.</p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Close the day */}
      {alreadyClosed ? (
        <div className="card p-6 flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0 mt-0.5" />
          <div>
            <h2 className="font-semibold text-slate-900">This day is closed</h2>
            <p className="text-sm text-slate-500 mt-1">
              Drawer counted {money(data.closure.cashCountedCents)} against {money(data.closure.cashExpectedCents)} expected —{' '}
              <span className={clsx('font-medium', data.closure.overShortCents === 0 ? 'text-emerald-600' : 'text-amber-600')}>
                {data.closure.overShortCents === 0
                  ? 'exactly even'
                  : `${data.closure.overShortCents > 0 ? 'over' : 'short'} ${money(Math.abs(data.closure.overShortCents))}`}
              </span>
              {data.closure.notes ? <> · “{data.closure.notes}”</> : null}
            </p>
          </div>
        </div>
      ) : (
        <div className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Banknote className="w-5 h-5 text-primary-600" />
            Close the day
          </h2>
          <div className="flex items-center justify-between text-sm bg-slate-50 rounded-xl px-4 py-3">
            <span className="text-slate-600">Cash expected in the drawer</span>
            <span className="font-semibold text-slate-900">{money(cashExpected)}</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Cash counted ($)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={cashCounted}
              onChange={(e) => setCashCounted(e.target.value)}
              placeholder="0.00"
              className="input w-full"
            />
            {cashCounted !== '' && (
              <p className={clsx('text-sm mt-2 flex items-center gap-1.5', overShort === 0 ? 'text-emerald-600' : 'text-amber-600')}>
                {overShort === 0 ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                {overShort === 0
                  ? 'Exactly even.'
                  : `${overShort > 0 ? 'Over' : 'Short'} by ${money(Math.abs(overShort))}.`}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. $20 paid out for produce run"
              className="input w-full"
            />
          </div>

          <button
            onClick={() => closeMutation.mutate()}
            disabled={closeMutation.isPending || cashCounted === ''}
            className="w-full py-3 rounded-xl bg-primary-600 text-white font-semibold disabled:opacity-50"
          >
            {closeMutation.isPending ? 'Closing…' : 'Close the day'}
          </button>
        </div>
      )}

      {/* Recent closures */}
      {closures && closures.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-3">Recent closes</h2>
          <div className="divide-y divide-slate-100">
            {closures.map((c) => (
              <div key={c.id} className="py-2.5 flex items-center justify-between text-sm">
                <span className="text-slate-600">{c.businessDate}</span>
                <span className="text-slate-500">
                  {money(c.summary?.totals?.collectedCents || 0)} collected
                </span>
                <span className={clsx('font-medium', c.overShortCents === 0 ? 'text-emerald-600' : 'text-amber-600')}>
                  {c.overShortCents === 0 ? 'even' : `${c.overShortCents > 0 ? '+' : '−'}${money(Math.abs(c.overShortCents))}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
